import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Bot, ChevronDown, Sparkles, ExternalLink, CheckCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { getBackendUrl } from '../services/api';

interface Message {
    id: string;
    role: 'user' | 'bot';
    content: string;
    timestamp: Date;
    leadCaptured?: boolean;
}

const QUICK_PROMPTS = [
    { label: "📊 2-Way Google Sheets Sync", query: "How does the 2-way Google Sheets sync work for WhatsApp?" },
    { label: "💰 Pricing Plans", query: "What are the pricing tiers (₦45,000, ₦110,000, ₦280,000) and what features are included?" },
    { label: "🛡️ Official Meta Cloud API", query: "How does Reach use the official Meta Cloud API to prevent phone number bans?" },
    { label: "📱 Try Live WhatsApp Demo", query: "Can I test Reach on my phone right now?" }
];

export const LandingPageChatbot: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [messages, setMessages] = useState<Message[]>([
        {
            id: '1',
            role: 'bot',
            content: "Hi there! 👋 I'm **Reach AI**. \n\nI can show you how to automate your WhatsApp marketing, sync customer replies with **Google Sheets**, or set up smart **AI Agents**. \n\nWhat would you like to explore?",
            timestamp: new Date()
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    useEffect(() => {
        // Show notification & badge after 3 seconds if chat hasn't been opened
        const timer = setTimeout(() => {
            if (!isOpen) {
                setShowNotification(true);
                setHasUnread(true);
            }
        }, 3000);
        return () => clearTimeout(timer);
    }, [isOpen]);

    const handleOpenChat = () => {
        setIsOpen(true);
        setShowNotification(false);
        setHasUnread(false);
    };

    const sendMessage = async (textToSend: string) => {
        if (!textToSend.trim()) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: textToSend,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsTyping(true);

        try {
            const response = await fetch(`${getBackendUrl()}/api/public/landing-chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    message: userMessage.content,
                    history: messages.map(m => ({ role: m.role, content: m.content }))
                })
            });

            if (!response.ok) {
                throw new Error('Failed to get response');
            }

            const data = await response.json();
            
            const botMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: 'bot',
                content: data.reply,
                timestamp: new Date(),
                leadCaptured: data.leadCaptured
            };

            setMessages(prev => [...prev, botMessage]);
        } catch (error) {
            console.error('Chat error:', error);
            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: 'bot',
                content: 'Sorry, I am having trouble connecting right now. You can also explore our [Pricing](/pricing) or [Sign Up](/register) directly.',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleSend = () => {
        sendMessage(inputValue);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl w-[350px] sm:w-[420px] h-[550px] max-h-[85vh] flex flex-col mb-4 overflow-hidden"
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-emerald-950/80 via-teal-900/60 to-zinc-900/90 p-4 border-b border-white/10 flex justify-between items-center backdrop-blur-md">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white shadow-lg shadow-teal-500/20 ring-1 ring-white/20">
                                    {/* Reach Brand Insignia */}
                                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                                        <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
                                        <path d="M12 9v1.5M14.5 13.5l-1.2-.7M9.5 13.5l1.2-.7" strokeWidth="1.5" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="flex items-center gap-1.5">
                                        <h3 className="text-white font-semibold text-sm">Reach Copilot</h3>
                                        <span className="text-[10px] font-mono bg-teal-500/20 text-teal-300 border border-teal-500/30 px-1.5 py-0.2 rounded font-medium">Assistant</span>
                                    </div>
                                    <div className="text-[10px] text-teal-400 flex items-center gap-1.5 uppercase tracking-wider font-semibold">
                                        <span className="w-1.5 h-1.5 bg-teal-400 rounded-full animate-pulse"></span> Active
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                <a
                                    href="/pricing"
                                    className="text-xs text-zinc-400 hover:text-white px-2 py-1 rounded hover:bg-white/5 transition-colors font-mono"
                                >
                                    Pricing
                                </a>
                                <button 
                                    onClick={() => setIsOpen(false)}
                                    aria-label="Minimize live chat window"
                                    className="text-gray-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/5"
                                >
                                    <ChevronDown className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Messages Area */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3.5 bg-[#050505]">
                            {messages.map((msg) => (
                                <div 
                                    key={msg.id} 
                                    className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
                                >
                                    <div className={`max-w-[88%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                                        msg.role === 'user' 
                                            ? 'bg-emerald-600 text-white rounded-tr-sm' 
                                            : 'bg-[#121212] text-gray-200 border border-white/10 rounded-tl-sm prose prose-invert prose-sm prose-p:leading-relaxed prose-pre:p-0 max-w-none'
                                    }`}>
                                        <ReactMarkdown
                                            components={{
                                                a: ({ node: _, ...props }) => (
                                                    <a 
                                                        {...props} 
                                                        className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 font-medium underline underline-offset-2" 
                                                        target={props.href?.startsWith('http') ? "_blank" : undefined}
                                                        rel="noopener noreferrer" 
                                                    />
                                                ),
                                                p: ({ node: _, ...props }) => <p {...props} className="m-0 mb-2 last:mb-0" />,
                                                ul: ({ node: _, ...props }) => <ul {...props} className="m-0 pl-4 list-disc mb-2 last:mb-0" />,
                                                ol: ({ node: _, ...props }) => <ol {...props} className="m-0 pl-4 list-decimal mb-2 last:mb-0" />,
                                                li: ({ node: _, ...props }) => <li {...props} className="m-0" />
                                            }}
                                        >
                                            {msg.content}
                                        </ReactMarkdown>
                                    </div>
                                    {msg.leadCaptured && (
                                        <div className="flex items-center gap-1.5 text-[11px] text-emerald-400 mt-1 pl-1 font-mono">
                                            <CheckCircle className="w-3.5 h-3.5" />
                                            <span>Contact details logged for onboarding followup!</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {isTyping && (
                                <div className="flex justify-start">
                                    <div className="bg-[#121212] border border-white/10 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1.5">
                                        <span className="text-[11px] text-zinc-400 font-mono mr-1">Reach AI is replying</span>
                                        <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                        <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                        <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Interactive Quick-Action Chips */}
                        <div className="px-3 py-2 bg-[#090909] border-t border-white/5 flex gap-1.5 overflow-x-auto no-scrollbar">
                            {QUICK_PROMPTS.map((chip, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => sendMessage(chip.query)}
                                    disabled={isTyping}
                                    className="whitespace-nowrap text-[11px] font-medium bg-white/[0.04] hover:bg-emerald-500/15 border border-white/10 hover:border-emerald-500/30 text-zinc-300 hover:text-emerald-300 px-2.5 py-1 rounded-full transition-colors shrink-0 disabled:opacity-50"
                                >
                                    {chip.label}
                                </button>
                            ))}
                        </div>

                        {/* Input Area */}
                        <div className="p-3 bg-[#0a0a0a] border-t border-white/10">
                            <div className="relative flex items-center">
                                <input
                                    type="text"
                                    aria-label="Type your message to Reach AI"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    placeholder="Ask anything or share your email/phone..."
                                    className="w-full bg-[#141414] border border-white/10 rounded-full pl-4 pr-12 py-3 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 transition-all"
                                />
                                <button
                                    onClick={handleSend}
                                    disabled={!inputValue.trim() || isTyping}
                                    aria-label="Send message to Reach AI"
                                    title="Send message"
                                    className="absolute right-2 p-2 bg-emerald-500 hover:bg-emerald-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-white rounded-full transition-colors shadow-sm"
                                >
                                    <Send className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            <div className="mt-2 flex items-center justify-between px-1 text-[10px] text-zinc-500 font-mono">
                                <span>⚡ Powered by Bytecohort Reach</span>
                                <a 
                                    href="/register" 
                                    className="text-emerald-400 hover:underline flex items-center gap-0.5"
                                >
                                    Free Trial <ExternalLink className="w-2.5 h-2.5" />
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showNotification && !isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="mb-4 bg-[#111] border border-emerald-500/30 p-4 rounded-2xl shadow-2xl cursor-pointer relative group w-72 mr-1 backdrop-blur-md ring-1 ring-emerald-500/20"
                        onClick={handleOpenChat}
                    >
                        <button 
                            onClick={(e) => { e.stopPropagation(); setShowNotification(false); setHasUnread(false); }}
                            aria-label="Dismiss chat prompt"
                            title="Dismiss notification"
                            className="absolute -top-2 -right-2 bg-gray-800 text-gray-400 hover:text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity border border-white/10"
                        >
                            <X className="w-3 h-3" />
                        </button>
                        <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white shrink-0 shadow-lg shadow-teal-500/20 ring-1 ring-white/20">
                                <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                                    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs text-white font-semibold flex items-center gap-1.5">
                                    Reach Team <span className="w-1.5 h-1.5 bg-teal-400 rounded-full animate-pulse"></span>
                                </p>
                                <p className="text-xs text-gray-300 mt-1 leading-snug">
                                    Have questions about WhatsApp campaigns or pricing? Let's chat!
                                </p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="relative">
                {hasUnread && !isOpen && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-teal-400 text-[10px] font-bold text-black rounded-full flex items-center justify-center ring-2 ring-black animate-pulse z-10">
                        1
                    </span>
                )}
                <button
                    onClick={() => {
                        setIsOpen(!isOpen);
                        if (!isOpen) {
                            setShowNotification(false);
                            setHasUnread(false);
                        }
                    }}
                    aria-label={isOpen ? "Close live chat window" : "Open live chat with Reach AI"}
                    title={isOpen ? "Close chat" : "Chat with Reach AI"}
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-2xl transition-all hover:scale-105 active:scale-95 ${
                        isOpen 
                            ? 'bg-[#111] border border-white/10 text-gray-400 hover:text-white' 
                            : 'bg-gradient-to-br from-teal-500 via-emerald-500 to-blue-600 text-white shadow-teal-500/30 ring-1 ring-white/20'
                    }`}
                >
                    {isOpen ? (
                        <X className="w-6 h-6" />
                    ) : (
                        <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                            <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
                        </svg>
                    )}
                </button>
            </div>
        </div>
    );
};
